import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Alert,
  Badge,
  Divider,
} from '@mui/material';
import {
  Search,
  MoreVert,
  Reply,
  Delete,
  MarkEmailUnread,
  MarkEmailRead,
  FilterList,
  Message,
  Person,
  Business,
  Warning,
  CheckCircle,
  Schedule,
  AttachMoney,
  Build,
  Star,
  Group,
} from '@mui/icons-material';
import axios from 'axios';
import { mockApi } from '../../services/mockData';

interface Conversation {
  _id: string;
  participants: Array<{
    id: string;
    name: string;
    email: string;
    type: 'installer' | 'service_tech' | 'admin';
    avatar?: string;
  }>;
  title: string;
  type: 'direct' | 'group' | 'team';
  region: string;
  lastMessage: {
    content: string;
    from: {
      id: string;
      name: string;
    };
    sentAt: string;
  };
  unreadCount: number;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
}

const Messages: React.FC = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [tabValue, setTabValue] = useState(0);

  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const messages = await mockApi.getMessages();
      
      // Convert messages to conversations format
      const conversations = messages.map(msg => ({
        _id: msg._id,
        participants: [
          { id: '1', name: msg.from, email: msg.from + '@email.com', type: msg.fromType === 'customer' ? 'installer' as const : 'admin' as const },
          { id: '2', name: msg.to, email: msg.to + '@email.com', type: msg.fromType === 'customer' ? 'admin' as const : 'installer' as const }
        ],
        lastMessage: {
          content: msg.message,
          from: { id: '1', name: msg.from },
          sentAt: msg.timestamp
        },
        subject: msg.subject,
        title: msg.subject,
        category: msg.category,
        priority: msg.priority === 'urgent' ? 'urgent' as const : msg.priority === 'high' ? 'high' as const : msg.priority === 'medium' ? 'medium' as const : 'low' as const,
        status: msg.status === 'unread' ? 'active' as const : 'archived' as const,
        region: msg.region,
        type: msg.category === 'installation' ? 'direct' as const : 'team' as const,
        unreadCount: msg.status === 'unread' ? 1 : 0,
        createdAt: msg.timestamp,
        updatedAt: msg.timestamp
      }));
      
      setConversations(conversations);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch conversations');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, priorityFilter, typeFilter, regionFilter]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, conversation: Conversation) => {
    setAnchorEl(event.currentTarget);
    setSelectedConversation(conversation);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedConversation(null);
  };

  const handleViewConversation = async (conversation: Conversation) => {
    try {
      const response = await axios.get(`/api/chat/${conversation._id}`);
      setSelectedConversation(response.data);
      fetchConversations(); // Refresh to update read status
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch conversation');
    }
  };

  const handleMarkAsRead = async () => {
    if (selectedConversation) {
      try {
        await mockApi.markConversationRead(selectedConversation._id);
        fetchConversations();
        handleMenuClose();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to mark conversation as read');
      }
    }
  };

  const handleMarkAsUnread = async () => {
    if (selectedConversation) {
      try {
        // For now, just refresh - unread functionality can be added later
        fetchConversations();
        handleMenuClose();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to mark conversation as unread');
      }
    }
  };

  const handleReply = () => {
    setReplyDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConversation = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const confirmDelete = async () => {
    if (selectedConversation) {
      try {
        // For now, just remove from local state - delete API can be added later
        setConversations(conversations.filter(c => c._id !== selectedConversation._id));
        setDeleteDialogOpen(false);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete conversation');
      }
    }
  };

  const sendReply = async () => {
    if (selectedConversation && replyContent.trim()) {
      try {
        await mockApi.sendMessage(selectedConversation._id, {
          content: replyContent
        });
        setReplyDialogOpen(false);
        setReplyContent('');
        fetchConversations();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to send message');
      }
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent': return <Warning />;
      case 'high': return <Warning />;
      case 'medium': return <Schedule />;
      case 'low': return <CheckCircle />;
      default: return <Message />;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'service_request': return <Build />;
      case 'quote_request': return <AttachMoney />;
      case 'complaint': return <Warning />;
      case 'feedback': return <Star />;
      case 'internal': return <Person />;
      case 'reply': return <Reply />;
      default: return <Message />;
    }
  };

  const getFromTypeIcon = (type: string) => {
    switch (type) {
      case 'customer': return <Person />;
      case 'team': return <Business />;
      case 'admin': return <Person />;
      default: return <Person />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUnreadCount = () => {
    return conversations.reduce((total, conv) => total + conv.unreadCount, 0);
  };

  const getUrgentCount = () => {
    return conversations.filter(c => c.priority === 'urgent').length;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ color: '#D4AF37', mb: 3 }}>
        💬 Company Chat
        {getUnreadCount() > 0 && (
          <Badge badgeContent={getUnreadCount()} color="error" sx={{ ml: 2 }}>
            <Message />
          </Badge>
        )}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="unread">Unread</MenuItem>
                  <MenuItem value="read">Read</MenuItem>
                  <MenuItem value="sent">Sent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priorityFilter}
                  label="Priority"
                  onChange={(e) => setPriorityFilter(e.target.value)}
                >
                  <MenuItem value="">All Priorities</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Region</InputLabel>
                <Select
                  value={regionFilter}
                  label="Region"
                  onChange={(e) => setRegionFilter(e.target.value)}
                >
                  <MenuItem value="">All Regions</MenuItem>
                  <MenuItem value="Denver">Denver</MenuItem>
                  <MenuItem value="Boise">Boise</MenuItem>
                  <MenuItem value="Spokane">Spokane</MenuItem>
                  <MenuItem value="Olympia">Olympia</MenuItem>
                  <MenuItem value="Company-wide">Company-wide</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                  setPriorityFilter('');
                  setTypeFilter('');
                  setRegionFilter('');
                }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Conversation List */}
      <Grid container spacing={3}>
        {conversations.map((conversation) => (
          <Grid item xs={12} key={conversation._id}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                border: conversation.unreadCount > 0 ? '2px solid #D4AF37' : '1px solid #333333',
                backgroundColor: conversation.unreadCount > 0 ? '#2C2C2C' : '#1A1A1A',
                '&:hover': {
                  border: '2px solid #D4AF37',
                  transform: 'translateY(-2px)',
                  transition: 'all 0.3s ease',
                },
              }}
              onClick={() => handleViewConversation(conversation)}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ bgcolor: '#D4AF37' }}>
                      {conversation.participants.length > 2 ? <Group /> : <Person />}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                        {conversation.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {conversation.participants.map(p => p.name).join(', ')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      icon={getPriorityIcon(conversation.priority)}
                      label={conversation.priority.toUpperCase()}
                      color={getPriorityColor(conversation.priority) as any}
                      size="small"
                    />
                    <Chip
                      label={conversation.type.toUpperCase()}
                      color="default"
                      size="small"
                    />
                    <Chip
                      label={conversation.region}
                      color="primary"
                      size="small"
                      sx={{ backgroundColor: '#D4AF37', color: '#000000' }}
                    />
                    {conversation.unreadCount > 0 && (
                      <Chip
                        label={conversation.unreadCount}
                        color="error"
                        size="small"
                      />
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuClick(e, conversation);
                      }}
                    >
                      <MoreVert />
                    </IconButton>
                  </Box>
                </Box>

                <Typography variant="h6" gutterBottom sx={{ color: '#D4AF37' }}>
                  {conversation.lastMessage.from.name}: {conversation.lastMessage.content}
                </Typography>

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(conversation.lastMessage.sentAt)}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    {conversation.unreadCount > 0 && (
                      <Chip
                        label="New Messages"
                        size="small"
                        color="error"
                      />
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Conversation Detail Dialog */}
      <Dialog 
        open={!!selectedConversation} 
        onClose={() => setSelectedConversation(null)}
        maxWidth="md"
        fullWidth
      >
        {selectedConversation && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: '#D4AF37' }}>
                  {selectedConversation.participants.length > 2 ? <Group /> : <Person />}
                </Avatar>
                <Box>
                  <Typography variant="h6">{selectedConversation.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedConversation.participants.map(p => p.name).join(', ')}
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box mb={2}>
                <Typography variant="h6" sx={{ color: '#D4AF37' }}>
                  Last Message
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(selectedConversation.lastMessage.sentAt)}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                <strong>{selectedConversation.lastMessage.from.name}:</strong> {selectedConversation.lastMessage.content}
              </Typography>

              <Box mt={2}>
                <Typography variant="h6" gutterBottom>
                  Participants
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {selectedConversation.participants.map((participant, index) => (
                    <Chip
                      key={index}
                      label={participant.name}
                      color="primary"
                      size="small"
                    />
                  ))}
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedConversation(null)}>Close</Button>
              <Button 
                onClick={handleReply}
                variant="contained"
                startIcon={<Reply />}
              >
                Reply
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => selectedConversation && handleViewConversation(selectedConversation)}>
          <ListItemIcon>
            <Message fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        {selectedConversation && selectedConversation.unreadCount > 0 ? (
          <MenuItem onClick={handleMarkAsRead}>
            <ListItemIcon>
              <MarkEmailRead fontSize="small" />
            </ListItemIcon>
            <ListItemText>Mark as Read</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem onClick={handleMarkAsUnread}>
            <ListItemIcon>
              <MarkEmailUnread fontSize="small" />
            </ListItemIcon>
            <ListItemText>Mark as Unread</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleReply}>
          <ListItemIcon>
            <Reply fontSize="small" />
          </ListItemIcon>
          <ListItemText>Reply</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteConversation}>
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Reply Dialog */}
      <Dialog open={replyDialogOpen} onClose={() => setReplyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Message</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={6}
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            placeholder="Type your reply here..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplyDialogOpen(false)}>Cancel</Button>
          <Button onClick={sendReply} variant="contained" disabled={!replyContent.trim()}>
            Send Reply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Conversation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this conversation? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Messages;
